<template>
  <div class="ai-composer">
    <div v-if="showSuggestions" class="ai-quick">
      <button
        v-for="question in suggestions"
        :key="question"
        type="button"
        class="ai-quick-chip"
        @click="pickSuggestion(question)"
      >
        {{ question }}
      </button>
    </div>

    <div v-if="files.length" class="ai-files">
      <span v-for="file in files" :key="fileKey(file)" class="ai-file">
        <i class="fas fa-file-alt"></i>
        <span class="ai-file-name">{{ file.name }}</span>
        <span class="ai-file-size">{{ size(file.size) }}</span>
        <button type="button" class="ai-file-remove" :aria-label="`Retirer ${file.name}`" @click="removeFile(file)">
          <i class="fas fa-times"></i>
        </button>
      </span>
    </div>

    <form class="ai-input-box" :class="{ focused }" @submit.prevent="submit">
      <input
        ref="fileInput"
        class="ai-file-input"
        type="file"
        multiple
        :accept="accept"
        @change="onFilesSelected"
      />
      <textarea
        ref="input"
        v-model="draft"
        class="ai-input"
        rows="1"
        :placeholder="placeholder"
        aria-label="Message à l'assistant"
        @input="autosize"
        @focus="focused = true"
        @blur="focused = false"
        @keydown.enter.exact.prevent="submit"
        @paste="onPaste"
      ></textarea>
      <div class="ai-input-tools">
        <div class="ai-input-left">
          <button type="button" class="ai-tool" title="Joindre un fichier" aria-label="Joindre un fichier" @click="openFilePicker">
            <i class="fas fa-paperclip"></i>
          </button>
          <button
            type="button"
            class="ai-tool"
            :class="{ active: showSuggestions }"
            title="Suggestions"
            aria-label="Afficher les suggestions"
            :aria-pressed="showSuggestions"
            @click="showSuggestions = !showSuggestions"
          >
            <i class="far fa-lightbulb"></i>
          </button>
        </div>
        <span class="ai-input-hint">Entrée pour envoyer · Maj+Entrée pour aller à la ligne</span>
        <button class="ai-send" type="submit" :disabled="!canSend" aria-label="Envoyer">
          <i class="fas fa-arrow-up"></i>
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { formatFileSize } from './assistant-format';

const MAX_INPUT_HEIGHT = 140;

export default {
  name: 'AssistantComposer',
  props: {
    busy: { type: Boolean, default: false },
    suggestions: { type: Array, default: () => [] },
    placeholder: { type: String, default: 'Posez une question ou décrivez une action…' },
  },
  emits: ['send'],
  data() {
    return {
      draft: '',
      files: [],
      focused: false,
      showSuggestions: false,
      accept: 'image/*,.pdf,.csv,.xls,.xlsx,.txt',
    };
  },
  computed: {
    canSend() {
      return !this.busy && (this.draft.trim().length > 0 || this.files.length > 0);
    },
  },
  methods: {
    size: formatFileSize,

    fileKey(file) {
      return `${file.name}-${file.size}-${file.lastModified}`;
    },

    focus() {
      this.$nextTick(() => this.$refs.input?.focus());
    },

    autosize() {
      const input = this.$refs.input;
      if (!input) return;
      input.style.height = 'auto';
      input.style.height = `${Math.min(input.scrollHeight, MAX_INPUT_HEIGHT)}px`;
    },

    addFiles(fileList) {
      const known = new Set(this.files.map(this.fileKey));
      const incoming = Array.from(fileList || []).filter((file) => !known.has(this.fileKey(file)));
      this.files = [...this.files, ...incoming];
      this.focus();
    },

    removeFile(file) {
      const key = this.fileKey(file);
      this.files = this.files.filter((item) => this.fileKey(item) !== key);
    },

    openFilePicker() {
      this.$refs.fileInput?.click();
    },

    onFilesSelected(event) {
      this.addFiles(event.target.files);
      event.target.value = '';
    },

    onPaste(event) {
      const pasted = Array.from(event.clipboardData?.files || []);
      if (pasted.length) {
        event.preventDefault();
        this.addFiles(pasted);
      }
    },

    pickSuggestion(question) {
      this.showSuggestions = false;
      this.draft = question;
      this.submit();
    },

    submit() {
      if (!this.canSend) return;
      this.$emit('send', { text: this.draft.trim(), files: this.files });
      this.draft = '';
      this.files = [];
      this.$nextTick(this.autosize);
    },
  },
};
</script>

<style scoped>
.ai-composer {
  padding: 10px 12px 12px;
  border-top: 1px solid var(--ai-line);
  background: var(--ai-surface);
}

.ai-quick {
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
  overflow-x: auto;
  scrollbar-width: none;
}

.ai-quick::-webkit-scrollbar {
  display: none;
}

.ai-quick-chip {
  flex: 0 0 auto;
  padding: 6px 10px;
  border: 1px solid var(--ai-line);
  border-radius: 999px;
  background: var(--ai-surface);
  color: #334155;
  font-size: 12px;
  cursor: pointer;
}

.ai-quick-chip:hover {
  border-color: var(--ai-primary-line);
  color: var(--ai-primary);
}

.ai-files {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.ai-file {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 5px 6px 5px 10px;
  border: 1px solid var(--ai-line);
  border-radius: 10px;
  background: var(--ai-soft);
  font-size: 12px;
  color: #334155;
}

.ai-file > i {
  color: var(--ai-primary);
}

.ai-file-name {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
}

.ai-file-size {
  color: var(--ai-faint);
}

.ai-file-remove {
  width: 20px;
  height: 20px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--ai-muted);
  font-size: 10px;
  cursor: pointer;
}

.ai-file-remove:hover {
  background: #fee2e2;
  color: #be123c;
}

.ai-input-box {
  border: 1px solid var(--ai-line-strong);
  border-radius: 14px;
  background: var(--ai-surface);
  transition: border-color 140ms ease, box-shadow 140ms ease;
}

.ai-input-box.focused {
  border-color: var(--ai-primary);
  box-shadow: 0 0 0 3px var(--ai-primary-ring);
}

.ai-file-input {
  display: none;
}

.ai-input {
  display: block;
  width: 100%;
  max-height: 140px;
  padding: 11px 13px 4px;
  border: 0;
  outline: none;
  resize: none;
  background: transparent;
  font-size: 14px;
  line-height: 1.45;
  color: var(--ai-ink);
}

.ai-input::placeholder {
  color: var(--ai-faint);
}

.ai-input-tools {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 6px 6px;
}

.ai-input-left {
  display: flex;
  gap: 2px;
}

.ai-tool {
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--ai-muted);
  font-size: 14px;
  cursor: pointer;
  transition: background-color 140ms ease, color 140ms ease;
}

.ai-tool:hover,
.ai-tool.active {
  background: var(--ai-primary-soft);
  color: var(--ai-primary);
}

.ai-input-hint {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11px;
  color: var(--ai-faint);
  text-align: right;
}

.ai-send {
  flex: 0 0 34px;
  height: 34px;
  border: 0;
  border-radius: 10px;
  background: var(--ai-primary);
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  transition: transform 140ms var(--ai-ease), filter 140ms ease, opacity 140ms ease;
}

.ai-send:hover:not(:disabled) {
  filter: brightness(1.1);
}

.ai-send:active:not(:disabled) {
  transform: scale(0.94);
}

.ai-send:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

@media (hover: none) {
  .ai-input-hint {
    visibility: hidden;
  }
}
</style>
